import React, { useContext, useMemo, useState } from "react";
import * as yup from "yup";
import clsx from "clsx";
import { FieldArray, Formik, FormikHelpers } from "formik";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { HvGrid } from "@hitachivantara/uikit-react-core";

import { useSnackbar } from "lib/provider/SnackbarProvider";
import usePreventNavigation from "lib/hooks/usePreventNavigation";
import { editSop, useSop } from "lib/api/sop";
import Container from "components/layout/Container";
import Loading from "components/layout/Loading";
import Banner from "components/common/Banner";
import { FormHeader } from "components/form";
import Details from "components/sop/SopAddEditDetails";
import Steps from "components/sop/SopAddEditSteps";
import { ModuleNames, ModulePolicyTypes } from "lib/api/userSettings";
import { UserDataContext } from "lib/context/UserDataContext";
import NotFound from "components/common/NotFound";
import useStyles from "./styles";

const EditSop: React.FC = () => {
  const { t } = useTranslation("sopAddEdit");
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [, setPreventNavigation] = usePreventNavigation();
  const [showBanner, setShowBanner] = useState(false);

  const { data: sopData, mutate } = useSop(id);

  const sections = [
    {
      label: t("scrollTo.details"),
      value: "details",
    },
    {
      label: t("scrollTo.steps"),
      value: "steps",
    },
  ];

  const validationSchema = useMemo(
    () =>
      yup.object({
        name: yup.string().trim().required(t("validation.required")),
        description: yup.string().trim().required(t("validation.required")),
        steps: yup
          .array()
          .of(
            yup.object().shape({
              name: yup.string().required(t("validation.required")),
              description: yup.string(),
              instructions: yup.string().required(t("validation.required")),
            })
          )
          .min(1)
          .required(t("validation.required")),
      }),
    [t]
  );

  const onSave = (data: SopFormData) => {
    return editSop({
      id,
      sop_name: data.name?.trim(),
      sop_desc: data.description?.trim(),
      sop_steps: data.steps?.map((step, index) => {
        const s = { ...step };

        Object.keys(step).forEach((key) => {
          s[key] =
            typeof step[key] === "string"
              ? (step[key] as string).trim()
              : step[key];
          return key;
        });
        return {
          order: index,
          ...s,
          id: undefined,
        };
      }),
    });
  };

  const onSubmitHandler = async (
    values: SopFormData,
    actions: FormikHelpers<SopFormData>
  ) => {
    try {
      mutate(await onSave(values));
      enqueueSnackbar(t("validation.success.edit"), { variant: "success" });
      setPreventNavigation(false);
      actions.setStatus({});
      history.push("/settings/sop");
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const errorCode = error?.response?.data?.error?.code;
      if (errorCode?.endsWith("_name_already_taken")) {
        actions.setStatus({
          serverSideErrors: {
            name: t(`validation.nameAlreadyTaken`),
          },
        });
      } else {
        setShowBanner(true);
      }
    }
  };

  return (
    <Formik
      initialStatus={{ serverSideErrors: {} }}
      initialValues={
        {
          name: sopData?.data.sop_name,
          description: sopData?.data.sop_desc,
          steps: sopData?.data.sop_steps,
        } as SopFormData
      }
      validationSchema={validationSchema}
      onSubmit={onSubmitHandler}
    >
      {(props) => {
        const { handleSubmit, isSubmitting } = props;

        const goBackHandler = () => {
          history.goBack();
        };

        const onBannerCloseHandler = () => {
          setShowBanner(false);
        };

        return (
          <Container className={classes.container} maxWidth="xl">
            {isSubmitting && (
              <Loading
                delay={500}
                loadingLabel={t("submitting")}
                className={clsx(classes.loading, classes.zIndex)}
              />
            )}
            <Banner
              open={showBanner}
              variant="error"
              showIcon
              label={t("validation.unknownError.edit")}
              onClose={onBannerCloseHandler}
            />
            <form onSubmit={handleSubmit} noValidate>
              <FormHeader
                title={sopData?.data.sop_name as string}
                sections={sections}
                onCancel={goBackHandler}
              />
              <HvGrid container spacing={0}>
                <HvGrid
                  className={classes.sectionMargin}
                  tabIndex={-1}
                  id={sections[0].value}
                  xs={12}
                  item
                >
                  <Details />
                </HvGrid>
                <HvGrid
                  className={classes.sectionMargin}
                  tabIndex={-1}
                  id={sections[1].value}
                  xs={12}
                  item
                >
                  <FieldArray
                    name="steps"
                    render={(arrayHelpers) => <Steps {...arrayHelpers} />}
                  />
                </HvGrid>
              </HvGrid>
            </form>
          </Container>
        );
      }}
    </Formik>
  );
};
export const SopEditAuth: React.FC = () => {
  const { hasModulePolicy } = useContext(UserDataContext);
  const hasRight = useMemo(() => {
    return hasModulePolicy(
      ModuleNames.incidentManagement,
      ModulePolicyTypes.sop
    );
  }, [hasModulePolicy]);
  return (
    <>
      {hasRight ? (
        <Container noHVContainer>
          <EditSop />
        </Container>
      ) : (
        <NotFound />
      )}
    </>
  );
};
export default EditSop;
